import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import Logo from "./Logo";
import { SITE, waLink } from "../data/site";
import { CATEGORIES } from "../data/products";

const Footer = () => (
  <footer className="grain relative bg-navy text-slate-300" data-testid="site-footer">
    <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 py-16">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo dark />
          <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-xs">
            Authorised 3M channel partner supplying cable jointing kits,
            terminations and accessories from 1.1kV to 66kV across India since
            1972.
          </p>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-safety">
            MSME Registered · PAN India Supply
          </p>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
            Product Range
          </h3>
          <ul className="mt-5 space-y-2.5">
            {CATEGORIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/products/${c.slug}`}
                  data-testid={`footer-product-link-${c.slug}`}
                  className="text-sm text-slate-400 transition-colors hover:text-safety"
                >
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
            Company
          </h3>
          <ul className="mt-5 space-y-2.5 text-sm">
            <li><Link to="/about" data-testid="footer-about-link" className="text-slate-400 hover:text-safety">About Us</Link></li>
            <li><Link to="/products" data-testid="footer-products-link" className="text-slate-400 hover:text-safety">All Products</Link></li>
            <li><Link to="/industries" data-testid="footer-industries-link" className="text-slate-400 hover:text-safety">Industries We Serve</Link></li>
            <li><Link to="/blog" data-testid="footer-blog-link" className="text-slate-400 hover:text-safety">Blog</Link></li>
            <li><Link to="/enquiry" data-testid="footer-enquiry-link" className="text-slate-400 hover:text-safety">Send Enquiry</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
            Reach Us
          </h3>
          <ul className="mt-5 space-y-3.5 text-sm">
            <li className="flex gap-3">
              <MapPin size={16} className="mt-0.5 shrink-0 text-safety" />
              <span className="text-slate-400">
                <span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Head Office</span>
                {SITE.addressLine1}<br />{SITE.addressLine2}
              </span>
            </li>
            <li className="flex gap-3">
              <MapPin size={16} className="mt-0.5 shrink-0 text-safety" />
              <span className="text-slate-400">
                <span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">Branch</span>
                {SITE.address2Line1}<br />{SITE.address2Line2}
              </span>
            </li>
            <li className="flex gap-3 items-center">
              <Phone size={16} className="shrink-0 text-safety" />
              <a href={SITE.phoneHref} data-testid="footer-phone-link" className="text-slate-400 hover:text-safety">
                {SITE.phoneDisplay}
              </a>
            </li>
            <li className="flex gap-3 items-center">
              <Phone size={16} className="shrink-0 text-safety" />
              <a href={SITE.landlineHref} data-testid="footer-landline-link" className="text-slate-400 hover:text-safety">
                {SITE.landlineDisplay} <span className="text-slate-600">(landline)</span>
              </a>
            </li>
            <li className="flex gap-3 items-start">
              <Mail size={16} className="mt-0.5 shrink-0 text-safety" />
              <a href={`mailto:${SITE.email}`} data-testid="footer-email-link" className="break-all text-slate-400 hover:text-safety">
                {SITE.email}
              </a>
            </li>
            <li className="flex gap-3 items-center">
              <Clock size={16} className="shrink-0 text-safety" />
              <span className="text-slate-400">{SITE.hours}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-12 border-t border-slate-800 pt-6" data-testid="footer-popular-searches">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Popular searches</p>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm">
          <Link to="/products/cable-jointing-kits" data-testid="footer-seo-link-cable-jointing-kits" className="text-slate-400 transition-colors hover:text-safety">Cable Jointing Kits</Link>
          <Link to="/products/cable-jointing-kits" data-testid="footer-seo-link-heat-shrink-joints" className="text-slate-400 transition-colors hover:text-safety">Heat Shrink Joints</Link>
          <Link to="/products/cable-jointing-kits" data-testid="footer-seo-link-ht-termination-kits" className="text-slate-400 transition-colors hover:text-safety">HT Termination Kits</Link>
          <Link to="/products/cable-jointing-kits" data-testid="footer-seo-link-lv-cable-joints" className="text-slate-400 transition-colors hover:text-safety">LV Cable Joints</Link>
        </div>
      </div>

      <div className="mt-14 flex flex-col gap-3 border-t border-slate-800 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          © {new Date().getFullYear()} Universal Engineers · All rights reserved
        </p>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          3M is a trademark of 3M Company · Supplies via authorised channel
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
